import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { AuthProvider } from './contexts/AuthContext'
import { DialogProvider } from './components/DialogProvider'
import { FirebaseProvider } from './contexts/FirebaseContext'
import ProtectedRoute from './components/ProtectedRoute'
import Layout from './components/Layout'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Questions from './pages/Questions'
import QuestionAdd from './pages/QuestionAdd'
import QuestionEdit from './pages/QuestionEdit'
import Categories from './pages/Categories'
import CategoryAdd from './pages/CategoryAdd'
import CategoryEdit from './pages/CategoryEdit'
import Users from './pages/Users'
import UserView from './pages/UserView'
import UserDetails from './pages/UserDetails'
import Settings from './pages/Settings'
// New nursing app features
import NursingBullets from './pages/NursingBullets'
import NursingBulletAdd from './pages/NursingBulletAdd'
import NursingBulletView from './pages/NursingBulletView'
import AddBulletPoints from './pages/AddBulletPoints'
import Leaderboard from './pages/Leaderboard'
import Updates from './pages/Updates'
import Alerts from './pages/Alerts'
import UserProfiles from './pages/UserProfiles'
import AddUpdate from './pages/AddUpdate'
import UpdateView from './pages/UpdateView'
import UpdateEdit from './pages/UpdateEdit'
import AddAlert from './pages/AddAlert'
import AlertView from './pages/AlertView'
import AlertEdit from './pages/AlertEdit'
import AddOnlineTest from './pages/AddOnlineTest'
import OnlineTestEdit from './pages/OnlineTestEdit'
import Subnotes from './pages/Subnotes'
import DebugSubnotes from './pages/DebugSubnotes'
import AddTestQuestions from './pages/AddTestQuestions'
import TestQuestions from './pages/TestQuestions'
import ViewOnlineTestQuestions from './pages/ViewOnlineTestQuestions'
import AddTest from './pages/AddTest'
import AddHomeContent from './pages/AddHomeContent'
import HomeContentView from './pages/HomeContentView'
import HomeContentEdit from './pages/HomeContentEdit'
import TestsManagement from './pages/TestsManagement'
import OnlineTests from './pages/OnlineTests'
import OnlineTestDetails from './pages/OnlineTestDetails'
import Reports from './pages/Reports'
import Home from './pages/Home'
import Subscription from './pages/Subscription'
import AddSubscriptionCards from './pages/AddSubscriptionCards'
import Purchases from './pages/Purchases'

function App() {
  return (
    <FirebaseProvider>
      <AuthProvider>
        <DialogProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <div className="min-h-screen bg-gray-100">
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<Login />} />

                {/* Protected Routes */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }>
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="home" element={<Home />} />
                  <Route path="dashboard" element={<Dashboard />} />

                  {/* Questions Routes */}
                  <Route path="questions" element={<Questions />} />
                  <Route path="questions/add" element={<QuestionAdd />} />
                  <Route path="questions/edit/:id" element={<QuestionEdit />} />

                  {/* Categories Routes */}
                  <Route path="categories" element={<Categories />} />
                  <Route path="categories/add" element={<CategoryAdd />} />
                  <Route path="categories/edit/:id" element={<CategoryEdit />} />

                  {/* Users Routes */}
                  <Route path="users" element={<Users />} />
                  <Route path="users/view/:id" element={<UserView />} />
                  <Route path="users/details/:userId" element={<UserDetails />} />
                  <Route path="purchases" element={<Purchases />} />


                  {/* Settings Routes */}
                  <Route path="settings" element={<Settings />} />

                  {/* Nursing Bullets Routes */}
                  <Route path="nursing-bullets" element={<NursingBullets />} />
                  <Route path="nursing-bullets/add" element={<NursingBulletAdd />} />
                  <Route path="nursing-bullets/edit/:id" element={<NursingBulletAdd />} />
                  <Route path="nursing-bullets/view/:id" element={<NursingBulletView />} />
                  <Route path="nursing-bullets/add-points/:bulletId" element={<AddBulletPoints />} />

                  {/* Leaderboard Routes */}
                  <Route path="leaderboard" element={<Leaderboard />} />

                  {/* Updates Routes */}
                  <Route path="updates" element={<Updates />} />
                  <Route path="updates/add" element={<AddUpdate />} />
                  <Route path="updates/edit/:id" element={<UpdateEdit />} />
                  <Route path="updates/view/:id" element={<UpdateView />} />

                  {/* Alerts Routes */}
                  <Route path="alerts" element={<Alerts />} />
                  <Route path="alerts/add" element={<AddAlert />} />
                  <Route path="alerts/edit/:id" element={<AlertEdit />} />
                  <Route path="alerts/view/:id" element={<AlertView />} />
                  <Route path="alerts/:id/subnotes" element={<Subnotes />} />
                  <Route path="alerts/:id/subnotes/debug" element={<DebugSubnotes />} />

                  <Route path="online-tests/add" element={<AddOnlineTest />} />
                  <Route path="online-test-edit/:testId" element={<OnlineTestEdit />} />
                  <Route path="add-questions/:testId" element={<AddTestQuestions />} />
                  <Route path="test-questions/:testId" element={<TestQuestions />} />
                  <Route path="online-test-questions/:testId" element={<ViewOnlineTestQuestions />} />
                  <Route path="add-test/:contentId" element={<AddTest />} />
                  <Route path="home-content/add" element={<AddHomeContent />} />
                  <Route path="home-content/:contentId/edit" element={<AddHomeContent />} />
                  <Route path="home-content/view/:id" element={<HomeContentView />} />
                  <Route path="tests-management/:contentId" element={<TestsManagement />} />
                  <Route path="online-tests" element={<OnlineTests />} />

                  {/* Reports Routes */}
                  <Route path="reports" element={<Reports />} />

                  {/* Subscription Routes */}
                  <Route path="subscription" element={<Subscription />} />
                  <Route path="subscription/:subscriptionId/add-cards" element={<AddSubscriptionCards />} />
                  <Route path="home-content/:contentId/add-cards" element={<AddSubscriptionCards />} />

                  {/* User Profiles Routes */}
                  <Route path="user-profiles" element={<UserProfiles />} />
                  <Route path="user-profiles/analytics" element={<UserProfiles />} />
                </Route>

                {/* Catch all route */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>

              {/* Toast notifications */}
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                  success: {
                    duration: 3000,
                    iconTheme: {
                      primary: '#22c55e',
                      secondary: '#fff',
                    },
                  },
                  error: {
                    duration: 4000,
                    iconTheme: {
                      primary: '#ef4444',
                      secondary: '#fff',
                    },
                  },
                }}
              />
            </div>
          </Router>
        </DialogProvider>
      </AuthProvider>
    </FirebaseProvider>
  )
}

export default App
